import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Assignment as AssignmentIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Folder as FolderIcon,
  VisibilityOff as HideThumbnailsIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon,
  ViewSidebar as ThumbnailsIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import {
  Alert,
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { blue, grey, purple, red, green, orange } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppDispatch, RootState } from 'app/store';
import { useWindowSize } from 'hooks/useWindowSize';
import { debounce } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MdCheckBox,
  MdClose,
  MdGesture,
  MdTextFields,
  MdCancel as CancelIcon,
  MdCheckCircle as CheckCircleIcon,
  MdHourglassEmpty as HourglassEmptyIcon
} from 'react-icons/md';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import axiosInstance from 'Services/APIService';
import {
  assignTemplate,
  clearTemplateAssignmentError,
  clearTemplateAssignmentSuccess,
  crudCategories,
  crudSubcategories,
  listTemplates
} from 'slices/payrolslices/documentTemplateSlice';
import { listScreenings, resetScreeningsState } from 'slices/payrolslices/PeopleSlice';
import AssignTempInputFieldOverlay from './Assign/AssignTempInputFieldOverlay';
import { formatDate } from './documentHelper/DocumentHelperFunctions';
import { InputField, Template } from './types';
import CategorySelector from './viewTempletsList/CategorySelector';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Types - UPDATED with avatar support
interface Employee {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  competency?: string;
  status?: boolean;
  employmentType?: string;
  isDelete?: boolean;
}

interface SubcategoryData {
  subcategoryId: string;
  subcategory: string;
  templates: Template[];
  count: number;
}

interface CategoryData {
  categoryId: string;
  category: string;
  subcategories: SubcategoryData[];
  totalTemplates: number;
}

const steps = ['Select Employees', 'Choose Template', 'Configure Fields'];

// Helper function to filter out undefined values
const isDefined = <T,>(value: T | undefined | null): value is T => {
  return value !== undefined && value !== null;
};

const AssignTemplatePage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  // **ENHANCED RESPONSIVE BREAKPOINTS**
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallMobile = useMediaQuery('(max-width:400px)');
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isLargeMobile = useMediaQuery(theme.breakpoints.between('xs', 'sm'));
  const windowSize = useWindowSize();

  // **RESPONSIVE SIZING SYSTEM - COMPACT SIZES**
  const getResponsiveSizes = useCallback(() => {
    if (isSmallMobile) {
      return {
        containerPadding: 0.5,
        cardPadding: 0.75,
        spacing: 0.75,
        fontSize: {
          title: '0.7rem',
          subtitle: '0.6rem',
          body: '0.55rem',
          caption: '0.5rem',
          button: '0.55rem'
        },
        iconSize: {
          small: 10,
          medium: 14,
          large: 24
        },
        cardHeight: 140,
        thumbnailSize: { width: 35, height: 50 },
        dialogWidth: 240,
        signatureCanvas: { width: 200, height: 60 }
      };
    } else if (isMobile) {
      return {
        containerPadding: 0.75,
        cardPadding: 1,
        spacing: 1,
        fontSize: {
          title: '0.75rem',
          subtitle: '0.65rem',
          body: '0.6rem',
          caption: '0.55rem',
          button: '0.6rem'
        },
        iconSize: {
          small: 12,
          medium: 16,
          large: 28
        },
        cardHeight: 160,
        thumbnailSize: { width: 45, height: 65 },
        dialogWidth: 280,
        signatureCanvas: { width: 220, height: 80 }
      };
    } else if (isTablet) {
      return {
        containerPadding: 1,
        cardPadding: 1.25,
        spacing: 1.25,
        fontSize: {
          title: '0.8rem',
          subtitle: '0.7rem',
          body: '0.65rem',
          caption: '0.6rem',
          button: '0.65rem'
        },
        iconSize: {
          small: 14,
          medium: 18,
          large: 32
        },
        cardHeight: 180,
        thumbnailSize: { width: 55, height: 75 },
        dialogWidth: 320,
        signatureCanvas: { width: 260, height: 100 }
      };
    } else {
      return {
        containerPadding: 1.25,
        cardPadding: 1.5,
        spacing: 1.5,
        fontSize: {
          title: '0.85rem',
          subtitle: '0.75rem',
          body: '0.7rem',
          caption: '0.65rem',
          button: '0.7rem'
        },
        iconSize: {
          small: 16,
          medium: 20,
          large: 36
        },
        cardHeight: 200,
        thumbnailSize: { width: 65, height: 90 },
        dialogWidth: 360,
        signatureCanvas: { width: 300, height: 120 }
      };
    }
  }, [isSmallMobile, isMobile, isTablet]);

  const sizes = getResponsiveSizes();

  // Redux selectors - UPDATED to use screenings
  const { screenings, loadingScreenings, screeningsError } = useSelector(
    (state: RootState) => state.people
  );
  const {
    loading: templatesLoading,
    templates,
    categories,
    availableCategories,
    availableSubcategories,
    error,
    templateAssigning,
    templateAssignmentError,
    templateAssignmentSuccess,
    templateAssignmentMessage,
    categoryLoading,
    subcategoryLoading
  } = useSelector((state: RootState) => state.documentTemplate);

  const selectedProjectId = useSelector(
    (state: RootState) => state.homeTilesServices?.selectedProjectId || ''
  );
  const selectedServiceTitle = useSelector(
    (state: RootState) => state.serviceSelected?.selectedServiceTitle || ''
  );

  // State management
  const [activeStep, setActiveStep] = useState(0);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<string[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Employee[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [categoryData, setCategoryData] = useState<CategoryData[]>([]);
  const [displayTemplates, setDisplayTemplates] = useState<Template[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
  const [cardPreviewUrls, setCardPreviewUrls] = useState<{ [key: string]: string }>({});
  const [templatePreviewUrl, setTemplatePreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showThumbnails, setShowThumbnails] = useState(true);
  const [currentPdfPage, setCurrentPdfPage] = useState(1);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [thumbnailsLoaded, setThumbnailsLoaded] = useState<Set<number>>(new Set());
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [tempFieldValue, setTempFieldValue] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);
  const [selectedField, setSelectedField] = useState<InputField | null>(null);
  const [signatureMode, setSignatureMode] = useState<'pad' | 'name' | 'standard'>('pad');
  const [typedName, setTypedName] = useState('');
  const [isStandardChecked, setIsStandardChecked] = useState(false);
  const [editedFields, setEditedFields] = useState<InputField[]>([]);
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState<null | HTMLElement>(null);
  const [pdfRenderTrigger, setPdfRenderTrigger] = useState(0);

  // Avatar state for screenings
  const [avatarUrls, setAvatarUrls] = useState<{ [key: string]: string | null }>({});

  // Refs
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const signatureCanvasRef = useRef<SignatureCanvas>(null);

  // Status mapping for consistent display
  const statusMap = {
    Active: {
      label: 'Active',
      bgcolor: green[500],
      color: '#fff',
      icon: CheckCircleIcon
    },
    Inactive: {
      label: 'Inactive',
      bgcolor: red[500],
      color: '#fff',
      icon: CancelIcon
    },
    Pending: {
      label: 'Pending',
      bgcolor: orange[500],
      color: '#fff',
      icon: HourglassEmptyIcon
    },
    Removed: {
      label: 'Removed',
      bgcolor: grey[500],
      color: '#fff',
      icon: CancelIcon
    }
  };

  const getFileTypeIcon = useCallback(
    (fileName: string) => {
      const extension = fileName?.split('.').pop()?.toLowerCase();
      switch (extension) {
        case 'pdf':
          return <PdfIcon sx={{ fontSize: sizes.iconSize.large, color: red[500] }} />;
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
          return <ImageIcon sx={{ fontSize: sizes.iconSize.large, color: blue[500] }} />;
        default:
          return <DescriptionIcon sx={{ fontSize: sizes.iconSize.large, color: grey[500] }} />;
      }
    },
    [sizes.iconSize.large]
  );

  const calculatePdfWidth = useCallback(() => {
    const sidebarWidth = showThumbnails && pdfNumPages > 1 ? sizes.thumbnailSize.width + 40 : 0;
    const padding = sizes.containerPadding * 16;
    const availableWidth = windowSize.width - sidebarWidth - padding;
    return Math.max(availableWidth * 0.95, isSmallMobile ? 240 : isMobile ? 280 : 350);
  }, [showThumbnails, pdfNumPages, windowSize, sizes, isSmallMobile, isMobile]);

  // UPDATED: Debounced search for employees using listScreenings
  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const payload = {
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          search: value || '',
          page_size: 50
        };
        dispatch(listScreenings(payload));
      }, 400),
    [dispatch, selectedProjectId, selectedServiceTitle]
  );

  // UPDATED: Load employees using listScreenings
  useEffect(() => {
    if (selectedProjectId && selectedServiceTitle) {
      dispatch(resetScreeningsState());
      debouncedSearch('');
    }

    const loadData = async () => {
      if (selectedProjectId && selectedServiceTitle) {
        try {
          await dispatch(
            crudCategories({
              action: 'get',
              group_id: selectedProjectId,
              service_id: selectedServiceTitle
            })
          );

          await dispatch(
            crudSubcategories({
              action: 'get',
              group_id: selectedProjectId,
              service_id: selectedServiceTitle
            })
          );

          await dispatch(
            listTemplates({
              group_id: selectedProjectId,
              service_id: selectedServiceTitle,
              category_id: null
            })
          );
        } catch (error) {
          console.error('Failed to load data:', error);
        }
      }
    };

    loadData();
    return () => debouncedSearch.cancel();
  }, [debouncedSearch, dispatch, selectedProjectId, selectedServiceTitle]);

  // Load avatars for screenings
  useEffect(() => {
    const fetchAvatars = async () => {
      const newAvatarUrls: { [key: string]: string | null } = {};
      for (const screening of screenings || []) {
        if (screening.avatar) {
          try {
            const response = await axiosInstance.get(
              `/avatar/get-avatar?path=${encodeURIComponent(screening.avatar)}`,
              { responseType: 'blob' }
            );
            const imageUrl = URL.createObjectURL(response.data);
            newAvatarUrls[screening.employeeId] = imageUrl;
          } catch (error) {
            console.error(`Error fetching avatar for ${screening.employeeName}:`, error);
            newAvatarUrls[screening.employeeId] = null;
          }
        } else {
          newAvatarUrls[screening.employeeId] = null;
        }
      }
      setAvatarUrls(newAvatarUrls);
    };

    if (screenings?.length > 0) fetchAvatars();

    return () => {
      Object.values(avatarUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url);
      });
    };
  }, [screenings]);

  // Process category data from Redux state
  useEffect(() => {
    let templatesData: Template[] = [];
    let processedCategoryData: CategoryData[] = [];

    if (templates && templates.length > 0) {
      templatesData = templates;
    } else if (categories && categories.length > 0) {
      templatesData = categories.flatMap((category) => category.templates ?? []).filter(isDefined);
    }

    if (templatesData.length > 0 && availableCategories.length > 0) {
      const categoryMap = new Map<string, CategoryData>();

      templatesData.forEach((template) => {
        if (!template || !template.category) return;

        const categoryName = template.category;
        const subcategoryName = template.subcategory || template.subCategory || 'General';

        const categoryObj = availableCategories.find((cat) => cat.category === categoryName);
        const subcategoryObj = availableSubcategories.find(
          (sub) => sub.subcategory === subcategoryName
        );

        const categoryId =
          categoryObj?.category_id || `cat-${categoryName}`.toLowerCase().replace(/\s+/g, '-');
        const subcategoryId =
          subcategoryObj?.subcategory_id ||
          `${categoryName}-${subcategoryName}`.toLowerCase().replace(/\s+/g, '-');

        if (!categoryMap.has(categoryName)) {
          categoryMap.set(categoryName, {
            categoryId: categoryId,
            category: categoryName,
            subcategories: [],
            totalTemplates: 0
          });
        }

        const categoryData = categoryMap.get(categoryName)!;
        let subcategoryData = categoryData.subcategories.find(
          (sub) => sub.subcategory === subcategoryName
        );

        if (!subcategoryData) {
          subcategoryData = {
            subcategoryId: subcategoryId,
            subcategory: subcategoryName,
            templates: [],
            count: 0
          };
          categoryData.subcategories.push(subcategoryData);
        }

        subcategoryData.templates.push(template);
        subcategoryData.count++;
        categoryData.totalTemplates++;
      });

      processedCategoryData = Array.from(categoryMap.values());
    }

    setDisplayTemplates(templatesData);
    setCategoryData(processedCategoryData);

    // Auto-expand first category and select first subcategory
    if (processedCategoryData.length > 0 && !selectedCategory) {
      const firstCategory = processedCategoryData[0];
      if (firstCategory && firstCategory.category) {
        setSelectedCategory(firstCategory.category);
        setSelectedCategoryId(firstCategory.categoryId);
        setExpandedCategories(new Set([firstCategory.category]));

        if (
          firstCategory.subcategories &&
          firstCategory.subcategories.length > 0 &&
          !selectedSubcategory
        ) {
          const firstSubcategory = firstCategory.subcategories[0];
          if (firstSubcategory && firstSubcategory.subcategory) {
            setSelectedSubcategory(firstSubcategory.subcategory);
            setSelectedSubcategoryId(firstSubcategory.subcategoryId);
          }
        }
      }
    }
  }, [templates, categories, availableCategories, availableSubcategories, templatesLoading]);

  // Filter templates by selected category and subcategory
  useEffect(() => {
    if (selectedCategory && selectedSubcategory && categoryData.length > 0) {
      const category = categoryData.find((cat) => cat.category === selectedCategory);
      if (category) {
        const subcategory = category.subcategories.find(
          (sub) => sub.subcategory === selectedSubcategory
        );
        if (subcategory) {
          setFilteredTemplates(subcategory.templates);
        }
      }
    } else {
      setFilteredTemplates(displayTemplates);
    }
  }, [selectedCategory, selectedSubcategory, categoryData, displayTemplates]);

  // Load card previews
  useEffect(() => {
    const fetchCardPreviews = async () => {
      const newPreviewUrls: { [key: string]: string } = {};
      for (const template of displayTemplates) {
        if (template.uploadedFile?.storedFilePath) {
          try {
            const encodedPath = encodeURIComponent(template.uploadedFile.storedFilePath);
            const response = await axiosInstance.get(`/avatar/get-avatar?path=${encodedPath}`, {
              responseType: 'blob'
            });
            const blobUrl = URL.createObjectURL(response.data);
            newPreviewUrls[template.documentId] = blobUrl;
          } catch (error) {
            console.error(`Error fetching card preview for ${template.documentName}:`, error);
          }
        }
      }
      setCardPreviewUrls(newPreviewUrls);
    };
    if (displayTemplates.length > 0) {
      fetchCardPreviews();
    }
    return () => {
      Object.values(cardPreviewUrls).forEach((url) => URL.revokeObjectURL(url));
    };
  }, [displayTemplates]);

  // Handle Redux assignment success/error
  useEffect(() => {
    if (templateAssignmentSuccess && templateAssignmentMessage) {
      setTimeout(() => {
        dispatch(clearTemplateAssignmentSuccess());
        navigate('/payroll/documents/templates');
      }, 3000);
    }
  }, [templateAssignmentSuccess, templateAssignmentMessage, dispatch, navigate]);

  useEffect(() => {
    if (templateAssignmentError) {
      setTimeout(() => {
        dispatch(clearTemplateAssignmentError());
      }, 6000);
    }
  }, [templateAssignmentError, dispatch]);

  // Category handlers
  const handleCategoryToggle = useCallback((categoryName: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  }, []);

  const handleSubcategorySelect = useCallback(
    (categoryName: string, subcategoryName: string) => {
      setSelectedCategory(categoryName);
      setSelectedSubcategory(subcategoryName);

      const categoryObj = categoryData.find((cat) => cat.category === categoryName);
      const subcategoryObj = categoryObj?.subcategories.find(
        (sub) => sub.subcategory === subcategoryName
      );

      if (categoryObj) setSelectedCategoryId(categoryObj.categoryId);
      if (subcategoryObj) setSelectedSubcategoryId(subcategoryObj.subcategoryId);

      setCategoryMenuAnchor(null);
    },
    [categoryData]
  );

  const handleCategoryMenuClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setCategoryMenuAnchor(event.currentTarget);
  }, []);

  const handleCategoryMenuClose = useCallback(() => {
    setCategoryMenuAnchor(null);
  }, []);

  // Event handlers
  const handleEmployeeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmployeeSearch(e.target.value);
    debouncedSearch(e.target.value);
  };

  // UPDATED: Handle employee toggle using screenings data with avatar support
  const handleToggleEmployee = useCallback(
    (id: string) => {
      setSelectedMemberIds((prev) => {
        const newSelected = prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id];
        const employeeData = newSelected
          .map((empId) => {
            const screening = screenings?.find((s) => s.employeeId === empId);
            return screening
              ? {
                  id: screening.employeeId,
                  name: screening.employeeName,
                  email: screening.email,
                  avatar: screening.avatar, // Include avatar in employee data
                  competency: screening.competency,
                  status: screening.status,
                  employmentType: screening.employmentType,
                  isDelete: screening.isDelete
                }
              : null;
          })
          .filter(Boolean) as Employee[];
        setSelectedEmployees(employeeData);
        return newSelected;
      });
    },
    [screenings]
  );

  // Helper function to get status display
  const getStatusDisplay = (screening: any) => {
    if (screening?.isDelete || screening?.isDeleted) {
      return 'Removed';
    }
    return screening.status ? 'Active' : 'Inactive';
  };

  // Helper function for competency color
  const getCompetencyColor = (competency: string) => {
    switch (competency?.toLowerCase()) {
      case 'competent':
        return 'success';
      case 'incompetent':
        return 'error';
      default:
        return 'default';
    }
  };

  const fetchTemplatePreview = async (template: Template) => {
    if (!template.uploadedFile?.storedFilePath) {
      return null;
    }
    try {
      setPreviewLoading(true);
      const encodedPath = encodeURIComponent(template.uploadedFile.storedFilePath);
      const response = await axiosInstance.get(`/avatar/get-avatar?path=${encodedPath}`, {
        responseType: 'blob'
      });
      const blobUrl = URL.createObjectURL(response.data);
      setTemplatePreviewUrl(blobUrl);
      return blobUrl;
    } catch (error) {
      console.error('Error fetching template preview:', error);
      return null;
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewModalOpen(false);
    setSelectedTemplate(null);
    setThumbnailsLoaded(new Set());
    setPdfNumPages(0);
    setCurrentPdfPage(1);
    setPdfRenderTrigger(0);
    if (templatePreviewUrl) {
      URL.revokeObjectURL(templatePreviewUrl);
      setTemplatePreviewUrl(null);
    }
  };

  // UPDATED: Template selection with default field generation for empty templates
  const handleTemplateSelect = async (template: Template) => {
    handleClosePreview();
    setSelectedTemplate(template);

    // Generate default input fields if template has no fields but has positioning tags
    let fieldsToEdit = [...(template.inputFields || [])];

    // If no input fields exist, create default ones for positioning
    if (!fieldsToEdit.length && template.uploadedFile) {
      // Create sample field positions for empty template visualization
      const defaultFields: InputField[] = [
        {
          id: 'default-name-field',
          fieldLabel: 'Full Name',
          type: 'text',
          isRequired: true,
          value: '',
          x: 100,
          y: 200,
          width: 150,
          height: 25,
          pageNumber: 1,
          xPercent: 20,
          yPercent: 25,
          widthPercent: 25,
          heightPercent: 3,
          isEmpEdit: false
        },
        {
          id: 'default-date-field',
          fieldLabel: 'Date',
          type: 'date',
          isRequired: true,
          value: '',
          x: 400,
          y: 200,
          width: 120,
          height: 25,
          pageNumber: 1,
          xPercent: 65,
          yPercent: 25,
          widthPercent: 20,
          heightPercent: 3,
          isEmpEdit: false
        },
        {
          id: 'default-signature-field',
          fieldLabel: 'Signature',
          type: 'signature',
          isRequired: true,
          value: '',
          x: 100,
          y: 400,
          width: 180,
          height: 50,
          pageNumber: 1,
          xPercent: 20,
          yPercent: 50,
          widthPercent: 30,
          heightPercent: 6,
          isEmpEdit: false
        }
      ];

      fieldsToEdit = defaultFields;
    }

    setEditedFields(fieldsToEdit);
    await fetchTemplatePreview(template);
    setActiveStep(2);
  };

  const handleFieldClick = useCallback(
    (field: InputField, mode?: React.MouseEvent | 'sign' | 'type') => {
      setSelectedField(field);
      setCurrentFieldId(field.id);
      setTempFieldValue(field.value?.toString() || '');
      if (field.type.toLowerCase() === 'signature') {
        setSignatureMode(mode === 'sign' ? 'pad' : mode === 'type' ? 'name' : 'pad');
        setShowEditDialog(true);
      } else {
        setEditingFieldId(field.id);
        setShowEditDialog(true);
      }
    },
    []
  );

  const handleFieldValueChange = (fieldId: string, value: string) => {
    setEditedFields((prev) =>
      prev.map((field) => (field.id === fieldId ? { ...field, value } : field))
    );
  };

  const handleSignatureSave = () => {
    if (!currentFieldId) return;
    let signatureData = '';
    switch (signatureMode) {
      case 'pad':
        if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
          signatureData = signatureCanvasRef.current.toDataURL('image/png');
        }
        break;
      case 'name':
        if (typedName.trim()) {
          const canvas = document.createElement('canvas');
          canvas.width = sizes.signatureCanvas.width;
          canvas.height = sizes.signatureCanvas.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = '#000000';
            ctx.font = `${isSmallMobile ? 12 : isMobile ? 14 : 16}px cursive`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
            signatureData = canvas.toDataURL('image/png');
          }
        }
        break;
      case 'standard':
        if (isStandardChecked) {
          const canvas = document.createElement('canvas');
          canvas.width = isSmallMobile ? 25 : 35;
          canvas.height = isSmallMobile ? 20 : 25;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.strokeStyle = '#4caf50';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            ctx.beginPath();
            ctx.moveTo(canvas.width * 0.25, canvas.height * 0.5);
            ctx.lineTo(canvas.width * 0.45, canvas.height * 0.75);
            ctx.lineTo(canvas.width * 0.75, canvas.width * 0.25);
            ctx.stroke();
            signatureData = canvas.toDataURL('image/png');
          }
        }
        break;
    }
    if (signatureData) {
      handleFieldValueChange(currentFieldId, signatureData);
      setShowEditDialog(false);
      setCurrentFieldId(null);
      setSignatureMode('pad');
      setTypedName('');
      setIsStandardChecked(false);
      if (signatureCanvasRef.current) {
        signatureCanvasRef.current.clear();
      }
    }
  };

  const handleFieldSave = () => {
    if (!currentFieldId || !selectedField) return;
    const newValue = tempFieldValue.trim();
    setEditedFields((prev) =>
      prev.map((field) =>
        field.id === currentFieldId
          ? { ...field, value: newValue, isEmpEdit: field.isRequired && !newValue }
          : field
      )
    );
    setShowEditDialog(false);
    setCurrentFieldId(null);
    setTempFieldValue('');
    setSelectedField(null);
  };

  // UPDATED: handleAssignTemplate with avatar support in payload
  const handleAssignTemplate = async () => {
    if (!selectedTemplate || selectedEmployees.length === 0) {
      return;
    }

    // UPDATED: Include avatar in employee payload
    const employeesWithAvatar = selectedEmployees.map((emp) => ({
      ...emp,
      avatar: emp.avatar || null // Ensure avatar is included
    }));

    dispatch(
      assignTemplate({
        selectedTemplate: {
          ...selectedTemplate,
          category_id: selectedCategoryId,
          subcategory_id: selectedSubcategoryId
        },
        selectedEmployees: employeesWithAvatar, // Send employees with avatar data
        editedFields,
        selectedProjectId,
        selectedServiceTitle
      })
    );
  };

  // UPDATED: EMPTY TEMPLATE CANVAS RENDERER
  const renderEmptyTemplateCanvas = () => {
    return (
      <Box
        ref={pdfContainerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: 'calc(60vh - 80px)', // Use viewport height minus header space
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1.5,
          bgcolor: '#fafafa',
          overflow: 'hidden', // Prevent nested scrolling
          backgroundImage: `
          linear-gradient(rgba(156, 163, 175, 0.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(156, 163, 175, 0.1) 1px, transparent 1px)
        `,
          backgroundSize: '20px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {/* Empty state message */}
        {(!editedFields || editedFields.length === 0) && (
          <Box sx={{ textAlign: 'center', opacity: 0.6, zIndex: 1 }}>
            <DescriptionIcon sx={{ fontSize: isSmallMobile ? 36 : 48, color: grey[400], mb: 1 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: sizes.fontSize.caption }}
            >
              Empty Template Canvas
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: sizes.fontSize.caption }}
            >
              Field overlays will appear here when positioned
            </Typography>
          </Box>
        )}

        {/* Render field overlays for empty template */}
        {editedFields &&
          editedFields.length > 0 &&
          editedFields
            .filter((field) => field.pageNumber === 1)
            .map((field) => (
              <AssignTempInputFieldOverlay
                key={field.id}
                field={field}
                selectedDocument={selectedTemplate ?? undefined}
                pdfContainerRef={pdfContainerRef as React.RefObject<HTMLDivElement>}
                handleFieldClick={handleFieldClick}
              />
            ))}
      </Box>
    );
  };

  // Employee selection step with enhanced info display
  const renderStep1 = () => (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: sizes.fontSize.title,
          fontWeight: 'bold',
          mb: sizes.spacing,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <PersonIcon sx={{ color: '#217346', fontSize: sizes.iconSize.medium }} /> Select Employees
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          p: sizes.cardPadding
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search employees by name or email..."
          value={employeeSearch}
          onChange={handleEmployeeSearchChange}
          sx={{
            mb: sizes.spacing,
            '& .MuiInputBase-input': {
              fontSize: sizes.fontSize.caption,
              py: isSmallMobile ? 0.5 : 0.75
            },
            '& .MuiInputLabel-root': {
              fontSize: sizes.fontSize.caption
            }
          }}
        />
        {selectedEmployees.length > 0 && (
          <Box sx={{ mb: sizes.spacing }}>
            <Typography
              variant="subtitle2"
              sx={{
                mb: 0.75,
                color: '#217346',
                fontSize: sizes.fontSize.caption,
                fontWeight: 600
              }}
            >
              Selected Employees ({selectedEmployees.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selectedEmployees.map((employee) => (
                <Chip
                  key={employee.id}
                  label={employee.name}
                  onDelete={() => handleToggleEmployee(employee.id)}
                  deleteIcon={
                    <CloseIcon sx={{ fontSize: `${sizes.iconSize.small}px !important` }} />
                  }
                  variant="outlined"
                  size="small"
                  sx={{
                    fontSize: sizes.fontSize.caption,
                    height: isSmallMobile ? 18 : 22,
                    '& .MuiChip-label': {
                      px: isSmallMobile ? 0.5 : 0.75
                    }
                  }}
                />
              ))}
            </Box>
            <Divider sx={{ my: sizes.spacing }} />
          </Box>
        )}

        {screeningsError && (
          <Alert
            severity="error"
            sx={{
              mb: sizes.spacing,
              fontSize: sizes.fontSize.caption,
              '& .MuiAlert-message': {
                fontSize: sizes.fontSize.caption
              }
            }}
          >
            {screeningsError}
          </Alert>
        )}

        {loadingScreenings ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <CircularProgress size={isSmallMobile ? 16 : 20} sx={{ color: '#217346' }} />
            <Typography
              sx={{
                mt: 1,
                color: 'text.secondary',
                fontSize: sizes.fontSize.caption
              }}
            >
              Loading employees...
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: isSmallMobile ? 250 : 350, overflow: 'auto' }}>
            {!screenings || screenings.length === 0 ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  textAlign: 'center',
                  py: 2,
                  fontSize: sizes.fontSize.caption
                }}
              >
                No employees found
              </Typography>
            ) : (
              <List sx={{ p: 0 }}>
                {screenings.map((screening) => {
                  const status = getStatusDisplay(screening);
                  const StatusIcon = statusMap[status]?.icon || CheckCircleIcon;

                  return (
                    <Box
                      key={screening.employeeId}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr', // mobile stacked
                          sm: '40px 50px 1.5fr 1fr 1fr 1fr' // desktop table-like
                        },
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:hover': { bgcolor: alpha('#217346', 0.04) }
                      }}
                    >
                      {/* 1. Checkbox */}
                      <Checkbox
                        checked={selectedMemberIds.includes(screening.employeeId)}
                        onChange={() => handleToggleEmployee(screening.employeeId)}
                        size="small"
                        sx={{
                          color: '#217346',
                          '&.Mui-checked': { color: '#217346' },
                          mx: 'auto'
                        }}
                      />

                      {/* 2. Avatar */}
                      <Avatar
                        src={avatarUrls[screening.employeeId] || undefined}
                        sx={{ width: 36, height: 36, mx: 'auto' }}
                      >
                        {screening.employeeName?.charAt(0)}
                      </Avatar>

                      {/* 3. Name + Email */}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600 }} noWrap>
                          {screening.employeeName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
                          {screening.email}
                        </Typography>
                      </Box>

                      {/* 4. Competency */}
                      <Box>
                        {screening.competency && (
                          <Chip
                            label={screening.competency}
                            size="small"
                            color={getCompetencyColor(screening.competency)}
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        )}
                      </Box>

                      {/* 5. Employment Type */}
                      <Box>
                        {screening.employmentType && (
                          <Chip
                            label={screening.employmentType}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        )}
                      </Box>

                      {/* 6. Status */}
                      <Chip
                        size="small"
                        icon={<StatusIcon fontSize="small" />}
                        label={status}
                        sx={{
                          fontSize: '0.7rem',
                          height: 22,
                          justifySelf: 'flex-start',
                          bgcolor: alpha(statusMap[status]?.bgcolor || grey[500], 0.12),
                          color: statusMap[status]?.bgcolor || grey[600],
                          border: `1px solid ${alpha(statusMap[status]?.bgcolor || grey[500], 0.3)}`
                        }}
                      />
                    </Box>
                  );
                })}
              </List>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  );

  // Template selection step remains similar but with compact sizes
  const renderStep2 = () => (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: sizes.fontSize.title,
          fontWeight: 'bold',
          mb: sizes.spacing,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <DescriptionIcon sx={{ color: '#217346', fontSize: sizes.iconSize.medium }} /> Choose
        Template
      </Typography>
      <Typography
        sx={{
          fontSize: sizes.fontSize.caption,
          color: 'text.secondary',
          mb: sizes.spacing,
          lineHeight: 1.3
        }}
      >
        Browse and select a template to assign to {selectedEmployees.length} employee(s) •{' '}
        {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''} available
      </Typography>
      <Grid container spacing={sizes.spacing}>
        {!isMobile && categoryData.length > 0 && (
          <Grid item sm={3}>
            <CategorySelector
              categoryData={categoryData}
              selectedCategory={selectedCategory}
              selectedSubcategory={selectedSubcategory}
              expandedCategories={expandedCategories}
              categoryMenuAnchor={categoryMenuAnchor}
              categoryMenuOpen={Boolean(categoryMenuAnchor)}
              onCategoryToggle={handleCategoryToggle}
              onSubcategorySelect={handleSubcategorySelect}
              onCategoryMenuClick={handleCategoryMenuClick}
              onCategoryMenuClose={handleCategoryMenuClose}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={isMobile || categoryData.length === 0 ? 12 : 9}>
          <Paper
            elevation={0}
            sx={{
              p: sizes.cardPadding,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              minHeight: isSmallMobile ? '50vh' : isMobile ? '55vh' : 'calc(100vh - 200px)'
            }}
          >
            {templatesLoading || categoryLoading || subcategoryLoading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress size={isSmallMobile ? 20 : 24} sx={{ color: '#217346' }} />
                <Typography
                  sx={{
                    mt: 1.5,
                    color: 'text.secondary',
                    fontSize: sizes.fontSize.caption
                  }}
                >
                  Loading templates...
                </Typography>
              </Box>
            ) : filteredTemplates.length > 0 ? (
              <Grid container spacing={sizes.spacing}>
                {filteredTemplates.map((template) => (
                  <Grid item xs={12} md={6} key={template.documentId}>
                    <Card
                      sx={{
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        p: 0,
                        borderRadius: 1,
                        boxShadow: 1,
                        cursor: 'pointer',
                        border: `1px solid ${theme.palette.divider}`,
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-1px)',
                          borderColor: theme.palette.primary.main
                        },
                        height: sizes.cardHeight,
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: isSmallMobile ? 3 : 4,
                          right: isSmallMobile ? 3 : 4,
                          zIndex: 3,
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: 0.5,
                          p: 0.125
                        }}
                      >
                        <Button
                          onClick={() => handleTemplateSelect(template)}
                          size="small"
                          sx={{
                            fontSize: sizes.fontSize.button,
                            minWidth: isSmallMobile ? 35 : 45,
                            height: isSmallMobile ? 16 : 20,
                            backgroundColor: '#217346',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#1a5d37'
                            },
                            py: 0.25
                          }}
                        >
                          Select
                        </Button>
                      </Box>
                      <Box
                        sx={{
                          width: isMobile ? '100%' : isSmallMobile ? 80 : 100,
                          height: isMobile ? (isSmallMobile ? 60 : 80) : '100%',
                          flexShrink: 0,
                          p: isSmallMobile ? 0.5 : 0.75
                        }}
                      >
                        {cardPreviewUrls[template.documentId] ? (
                          template.uploadedFile?.fileType === 'pdf' ? (
                            <Document
                              file={cardPreviewUrls[template.documentId]}
                              loading={<CircularProgress size={12} />}
                            >
                              <Page
                                pageNumber={1}
                                width={isSmallMobile ? 60 : isMobile ? 80 : 90}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                              />
                            </Document>
                          ) : (
                            <img
                              src={cardPreviewUrls[template.documentId]}
                              alt={template.documentName}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '4px'
                              }}
                            />
                          )
                        ) : (
                          <Box
                            sx={{
                              opacity: 0.6,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              height: '100%',
                              gap: 0.25
                            }}
                          >
                            {getFileTypeIcon(template.uploadedFile?.fileName || 'document')}
                            <Typography
                              sx={{
                                fontSize: sizes.fontSize.caption,
                                color: grey[600],
                                textAlign: 'center'
                              }}
                            >
                              {template.uploadedFile?.fileType?.toUpperCase() || 'FILE'}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                      <CardContent
                        sx={{
                          flex: 1,
                          p: isSmallMobile ? 0.5 : 0.75,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          '&:last-child': {
                            pb: isSmallMobile ? 0.5 : 0.75
                          }
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{
                              fontSize: sizes.fontSize.caption,
                              fontWeight: 600,
                              mb: 0.5,
                              lineHeight: 1.2,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {template.documentName}
                          </Typography>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.25}
                            sx={{ mb: 0.5 }}
                          >
                            <FolderIcon sx={{ fontSize: sizes.iconSize.small, color: grey[500] }} />
                            <Typography sx={{ fontSize: '0.45rem' }} color="text.secondary" noWrap>
                              {template.uploadedFile?.fileName || 'Unknown file'}
                            </Typography>
                          </Stack>
                          <Typography sx={{ fontSize: '0.45rem' }} color="text.secondary">
                            {template.inputFields?.length || 0} field
                            {template.inputFields?.length !== 1 ? 's' : ''}
                            {template.inputFields?.some((f) => f.isRequired) && ' • Required'}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 0.5 }}
                        >
                          <Stack direction="row" alignItems="center" spacing={0.25}>
                            <TimeIcon sx={{ fontSize: sizes.iconSize.small, color: grey[500] }} />
                            <Typography sx={{ fontSize: '0.45rem' }} color="text.secondary">
                              {formatDate(template.createdDate)}
                            </Typography>
                          </Stack>
                          <Chip
                            label={`v${template.version ?? 1}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.4rem',
                              height: isSmallMobile ? 12 : 14,
                              '& .MuiChip-label': {
                                px: 0.25
                              }
                            }}
                          />
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Stack
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ py: isSmallMobile ? 2 : 4, textAlign: 'center' }}
              >
                <DescriptionIcon
                  sx={{
                    fontSize: isSmallMobile ? 32 : isMobile ? 40 : 48,
                    color: grey[400]
                  }}
                />
                <Typography sx={{ fontSize: sizes.fontSize.caption }} color="text.secondary">
                  No Templates Found
                </Typography>
                <Typography sx={{ fontSize: '0.5rem' }} color="text.secondary">
                  {selectedCategory && selectedSubcategory
                    ? `No templates available in "${selectedCategory}" → "${selectedSubcategory}".`
                    : 'No templates available.'}
                </Typography>
              </Stack>
            )}
          </Paper>
        </Grid>
      </Grid>
      {isMobile && categoryData.length > 0 && (
        <CategorySelector
          categoryData={categoryData}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          expandedCategories={expandedCategories}
          categoryMenuAnchor={categoryMenuAnchor}
          categoryMenuOpen={Boolean(categoryMenuAnchor)}
          onCategoryToggle={handleCategoryToggle}
          onSubcategorySelect={handleSubcategorySelect}
          onCategoryMenuClick={handleCategoryMenuClick}
          onCategoryMenuClose={handleCategoryMenuClose}
        />
      )}
    </Box>
  );

  // UPDATED: Input field overlay renderer with enhanced tag positioning
  const renderInputFieldOverlay = useCallback(
    (field: InputField) => (
      <AssignTempInputFieldOverlay
        key={field.id}
        field={field}
        selectedDocument={selectedTemplate ?? undefined}
        pdfContainerRef={pdfContainerRef as React.RefObject<HTMLDivElement>}
        handleFieldClick={handleFieldClick}
      />
    ),
    [handleFieldClick, pdfRenderTrigger, selectedTemplate]
  );

  // Field configuration step with enhanced field overlay support
  const renderStep3 = () => (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontSize: sizes.fontSize.title,
          fontWeight: 'bold',
          mb: sizes.spacing,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}
      >
        <EditIcon sx={{ color: '#217346', fontSize: sizes.iconSize.medium }} /> Configure Fields
      </Typography>
      <Typography
        sx={{
          fontSize: sizes.fontSize.caption,
          color: 'text.secondary',
          mb: sizes.spacing,
          lineHeight: 1.3
        }}
      >
        Fill in or edit the required fields for the template. Click on any field to edit its value.
        {!selectedTemplate?.inputFields?.length &&
          ' Sample fields shown for positioning reference on empty canvas.'}
        {!templatePreviewUrl && ' Using empty canvas mode for field positioning.'}
      </Typography>
      <Box sx={{ minHeight: isSmallMobile ? '40vh' : 'calc(100vh - 180px)' }}>
        <Box
          sx={{
            display: 'flex',
            height: isSmallMobile ? '40vh' : '60vh',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            overflow: 'hidden'
          }}
        >
          {showThumbnails &&
            pdfNumPages > 1 &&
            selectedTemplate?.uploadedFile?.fileType === 'pdf' && (
              <Box
                sx={{
                  width: sizes.thumbnailSize.width + 16,
                  borderRight: '1px solid #e0e0e0',
                  bgcolor: '#fafafa',
                  overflowY: 'auto',
                  p: 0.25
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.45rem',
                    fontWeight: 600,
                    textAlign: 'center',
                    mb: 0.25,
                    display: 'block'
                  }}
                >
                  Pages ({pdfNumPages})
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    width: '100%',
                    alignItems: 'center'
                  }}
                >
                  {Array.from(new Array(pdfNumPages), (_, index) => (
                    <Box
                      key={`thumbnail-${index + 1}`}
                      onClick={() => setCurrentPdfPage(index + 1)}
                      sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        border:
                          currentPdfPage === index + 1 ? '2px solid #1976d2' : '1px solid #ddd',
                        borderRadius: 0.5,
                        bgcolor: currentPdfPage === index + 1 ? '#e3f2fd' : 'white',
                        width: sizes.thumbnailSize.width,
                        height: sizes.thumbnailSize.height,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <Document file={templatePreviewUrl}>
                        <Page
                          pageNumber={index + 1}
                          width={sizes.thumbnailSize.width - 6}
                          renderTextLayer={false}
                          renderAnnotationLayer={false}
                          loading={<CircularProgress size={8} />}
                          onRenderSuccess={() =>
                            setThumbnailsLoaded((prev) => new Set([...Array.from(prev), index + 1]))
                          }
                        />
                      </Document>
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0.5,
                          left: 0.5,
                          bgcolor: currentPdfPage === index + 1 ? '#1976d2' : 'rgba(0,0,0,0.7)',
                          color: 'white',
                          px: 0.125,
                          py: 0.0625,
                          borderRadius: 0.125,
                          fontSize: '0.4rem'
                        }}
                      >
                        {index + 1}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.divider}`,
                p: sizes.containerPadding * 0.75,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                bgcolor: grey[50]
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {selectedTemplate?.uploadedFile?.fileType === 'pdf' && pdfNumPages > 1 && (
                  <Tooltip title={showThumbnails ? 'Hide Thumbnails' : 'Show Thumbnails'}>
                    <IconButton
                      onClick={() => setShowThumbnails(!showThumbnails)}
                      size="small"
                      sx={{
                        bgcolor: 'white',
                        border: '1px solid #e0e0e0',
                        '&:hover': { bgcolor: grey[100] },
                        width: isSmallMobile ? 20 : 24,
                        height: isSmallMobile ? 20 : 24
                      }}
                    >
                      {showThumbnails ? (
                        <HideThumbnailsIcon sx={{ fontSize: sizes.iconSize.small }} />
                      ) : (
                        <ThumbnailsIcon sx={{ fontSize: sizes.iconSize.small }} />
                      )}
                    </IconButton>
                  </Tooltip>
                )}
                <Typography sx={{ fontSize: '0.5rem', color: 'text.secondary' }}>
                  {selectedTemplate?.uploadedFile?.fileType === 'pdf'
                    ? `Page ${currentPdfPage} of ${pdfNumPages}`
                    : 'Image Document'}
                </Typography>
                {editedFields && editedFields.length > 0 && (
                  <Chip
                    label={`${editedFields.length} fields`}
                    size="small"
                    sx={{
                      height: isSmallMobile ? 12 : 14,
                      fontSize: '0.45rem',
                      bgcolor: `${purple[500]}15`,
                      color: purple[500]
                    }}
                  />
                )}
              </Box>
              {selectedTemplate?.uploadedFile?.fileType === 'pdf' && pdfNumPages > 1 && (
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  <Button
                    onClick={() => setCurrentPdfPage(Math.max(1, currentPdfPage - 1))}
                    disabled={currentPdfPage <= 1}
                    size="small"
                    sx={{
                      minWidth: isSmallMobile ? 20 : 28,
                      fontSize: '0.5rem',
                      border: '1px solid #e0e0e0',
                      height: isSmallMobile ? 20 : 24
                    }}
                  >
                    ←
                  </Button>
                  <Button
                    onClick={() => setCurrentPdfPage(Math.min(pdfNumPages, currentPdfPage + 1))}
                    disabled={currentPdfPage >= pdfNumPages}
                    size="small"
                    sx={{
                      minWidth: isSmallMobile ? 20 : 28,
                      fontSize: '0.5rem',
                      border: '1px solid #e0e0e0',
                      height: isSmallMobile ? 20 : 24
                    }}
                  >
                    →
                  </Button>
                </Box>
              )}
            </Box>
            <Box
              ref={pdfContainerRef}
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: sizes.containerPadding * 0.75,
                bgcolor: grey[50],
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                minHeight: 0,
                position: 'relative'
              }}
            >
              {!templatePreviewUrl ? (
                // Show empty template canvas when no document is loaded
                renderEmptyTemplateCanvas()
              ) : (
                // Show normal document with overlays
                <Box sx={{ position: 'relative', width: 'fit-content', isolation: 'isolate' }}>
                  {selectedTemplate?.uploadedFile?.fileType === 'pdf' ? (
                    <Document
                      file={templatePreviewUrl}
                      onLoadSuccess={({ numPages }) => {
                        setPdfNumPages(numPages);
                        setTimeout(() => {
                          setPdfRenderTrigger((prev) => prev + 1);
                        }, 100);
                      }}
                      loading={<CircularProgress size={isSmallMobile ? 16 : 20} />}
                      error={
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography color="error" sx={{ fontSize: sizes.fontSize.caption }}>
                            Failed to load PDF
                          </Typography>
                        </Box>
                      }
                    >
                      <Page
                        pageNumber={currentPdfPage}
                        width={calculatePdfWidth()}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        onRenderSuccess={() => {
                          setTimeout(() => {
                            setPdfRenderTrigger((prev) => prev + 1);
                          }, 100);
                        }}
                      />
                    </Document>
                  ) : (
                    <img
                      src={templatePreviewUrl || ''}
                      alt={selectedTemplate?.documentName}
                      style={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain'
                      }}
                      onLoad={() => {
                        setPreviewLoading(false);
                        setPdfRenderTrigger((prev) => prev + 1);
                      }}
                    />
                  )}
                  {editedFields
                    ?.filter((field) =>
                      selectedTemplate?.uploadedFile?.fileType === 'pdf'
                        ? field.pageNumber === currentPdfPage
                        : field.pageNumber === 1
                    )
                    .map(renderInputFieldOverlay)}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Container
      maxWidth="xl"
      sx={{
        px: { xs: sizes.containerPadding, md: sizes.containerPadding * 1.25 },
        py: { xs: sizes.containerPadding, md: sizes.containerPadding * 1.5 }
      }}
    >
      <Box sx={{ mb: sizes.spacing }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          sx={{
            fontWeight: 600,
            color: 'primary.main',
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: sizes.fontSize.title
          }}
        >
          <AssignmentIcon sx={{ fontSize: sizes.iconSize.medium }} /> Assign Template
        </Typography>
        <Typography
          variant={isMobile ? 'body2' : 'h6'}
          color="text.secondary"
          sx={{ fontSize: sizes.fontSize.caption }}
        >
          Assign document templates to employees with custom field configurations
        </Typography>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!(error || templateAssignmentError)}
        autoHideDuration={6000}
        onClose={() => {
          if (templateAssignmentError) dispatch(clearTemplateAssignmentError());
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            if (templateAssignmentError) dispatch(clearTemplateAssignmentError());
          }}
          severity="error"
          sx={{ width: '100%', fontSize: sizes.fontSize.caption }}
        >
          {error || templateAssignmentError}
        </Alert>
      </Snackbar>

      {/* Success Snackbar */}
      <Snackbar
        open={!!(templateAssignmentSuccess && templateAssignmentMessage)}
        autoHideDuration={6000}
        onClose={() => dispatch(clearTemplateAssignmentSuccess())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => dispatch(clearTemplateAssignmentSuccess())}
          severity="success"
          sx={{ width: '100%', fontSize: sizes.fontSize.caption }}
        >
          {templateAssignmentMessage}
        </Alert>
      </Snackbar>

      {/* Stepper */}
      <Box
        sx={{
          p: {
            xs: sizes.cardPadding * 0.5,
            sm: sizes.cardPadding * 0.75,
            md: sizes.cardPadding
          },
          mb: sizes.spacing,
          borderRadius: 1
        }}
      >
        <Stepper
          activeStep={activeStep}
          alternativeLabel={!isMobile}
          sx={{
            '& .MuiStepConnector-line': {
              borderTopWidth: isSmallMobile ? 1 : 2,
              transition: 'all 0.2s ease'
            },
            '& .MuiStep-root': {
              padding: {
                xs: '2px 1px',
                sm: '4px 3px',
                md: '8px 6px'
              },
              minWidth: {
                xs: 50,
                sm: 65,
                md: 80
              }
            },
            '& .MuiStepIcon-root': {
              fontSize: {
                xs: sizes.iconSize.medium,
                sm: sizes.iconSize.medium + 2,
                md: sizes.iconSize.medium + 4
              },
              '&.Mui-active': { color: '#217346' },
              '&.Mui-completed': { color: '#4caf50' }
            },
            '& .MuiStepLabel-label': {
              fontSize: {
                xs: '0.5rem',
                sm: '0.55rem',
                md: '0.6rem'
              },
              fontWeight: 500,
              textAlign: 'center',
              lineHeight: 1.2,
              marginTop: { xs: '2px', sm: '3px', md: '4px' },
              '&.Mui-active': { color: '#217346', fontWeight: 600 },
              '&.Mui-completed': { color: '#4caf50', fontWeight: 500 }
            }
          }}
        >
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>
                <Typography
                  sx={{
                    fontSize: { xs: '0.5rem', sm: '0.55rem', md: '0.6rem' },
                    fontWeight: activeStep === index ? 600 : 500,
                    color:
                      activeStep === index
                        ? '#217346'
                        : activeStep > index
                        ? '#4caf50'
                        : 'text.secondary',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: isSmallMobile ? 'nowrap' : 'normal'
                  }}
                  title={label}
                >
                  {isSmallMobile && label.length > 6 ? `${label.substring(0, 4)}...` : label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Main Content */}
      <Box sx={{ mb: sizes.spacing, borderRadius: 1, minHeight: isSmallMobile ? '50vh' : '400px' }}>
        {activeStep === 0 && renderStep1()}
        {activeStep === 1 && renderStep2()}
        {activeStep === 2 && renderStep3()}
      </Box>

      {/* Navigation Buttons */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: sizes.cardPadding,
          bgcolor: '#f5f5f5',
          borderRadius: 1
        }}
      >
        <Button
          onClick={
            activeStep === 0
              ? () => navigate('/payroll/documents/templates')
              : () => setActiveStep((prev) => prev - 1)
          }
          sx={{
            fontSize: sizes.fontSize.button,
            color: '#666',
            '&:hover': { backgroundColor: 'transparent', color: '#333' },
            minHeight: isSmallMobile ? 24 : 32
          }}
          startIcon={
            activeStep === 0 ? (
              <CloseIcon sx={{ fontSize: sizes.iconSize.small }} />
            ) : (
              <ArrowBackIcon sx={{ fontSize: sizes.iconSize.small }} />
            )
          }
          size={isMobile ? 'small' : 'medium'}
        >
          {activeStep === 0 ? 'Cancel' : 'Back'}
        </Button>

        {activeStep < 2 ? (
          <Button
            variant="contained"
            onClick={() => {
              if (activeStep === 0 && selectedEmployees.length === 0) return;
              if (activeStep === 1 && !selectedTemplate) return;
              setActiveStep((prev) => prev + 1);
            }}
            disabled={
              (activeStep === 0 && selectedEmployees.length === 0) ||
              (activeStep === 1 && !selectedTemplate)
            }
            endIcon={<ArrowForwardIcon sx={{ fontSize: sizes.iconSize.small }} />}
            sx={{
              fontSize: sizes.fontSize.button,
              backgroundColor: '#217346',
              '&:hover': { backgroundColor: '#1a5d37' },
              minHeight: isSmallMobile ? 24 : 32
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleAssignTemplate}
            disabled={templateAssigning}
            startIcon={
              templateAssigning ? (
                <CircularProgress size={sizes.iconSize.small} />
              ) : (
                <CheckIcon sx={{ fontSize: sizes.iconSize.small }} />
              )
            }
            sx={{
              fontSize: sizes.fontSize.button,
              backgroundColor: '#217346',
              '&:hover': { backgroundColor: '#1a5d37' },
              minHeight: isSmallMobile ? 24 : 32
            }}
            size={isMobile ? 'small' : 'medium'}
          >
            {templateAssigning ? 'Assigning...' : 'Assign Template'}
          </Button>
        )}
      </Box>

      {/* Edit Field Dialog */}
      <Dialog
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 1,
            minHeight:
              selectedField?.type.toLowerCase() === 'signature'
                ? isMobile
                  ? 280
                  : 320
                : isMobile
                ? 140
                : 180,
            width:
              selectedField?.type.toLowerCase() === 'signature' ? undefined : isMobile ? 240 : 280
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 0.75,
            fontSize: isMobile ? '0.75rem' : '0.85rem'
          }}
        >
          {selectedField?.type.toLowerCase() === 'signature'
            ? 'Add Signature'
            : `Edit ${selectedField?.type || 'Field'}`}
          <IconButton
            onClick={() => setShowEditDialog(false)}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <MdClose size={12} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: isMobile ? 1 : 1.5 }}>
          {selectedField?.type.toLowerCase() === 'signature' ? (
            <>
              <Tabs
                value={signatureMode}
                onChange={(_, newValue) => setSignatureMode(newValue)}
                variant="fullWidth"
                sx={{
                  mb: 1.5,
                  '& .MuiTab-root': {
                    minHeight: 24,
                    fontSize: '0.6rem',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 0.5
                  },
                  '& .MuiTabs-indicator': { height: 1.5 }
                }}
              >
                <Tab icon={<MdGesture size={10} />} label="Draw" value="pad" iconPosition="start" />
                <Tab
                  icon={<MdTextFields size={10} />}
                  label="Type"
                  value="name"
                  iconPosition="start"
                />
                <Tab
                  icon={<MdCheckBox size={10} />}
                  label="Check"
                  value="standard"
                  iconPosition="start"
                />
              </Tabs>
              <Box
                sx={{
                  minHeight: isMobile ? 120 : 150,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {signatureMode === 'pad' && (
                  <Paper
                    elevation={1}
                    sx={{
                      p: 0.75,
                      borderRadius: 0.75,
                      border: `1px solid ${theme.palette.primary.main}20`
                    }}
                  >
                    <SignatureCanvas
                      ref={signatureCanvasRef}
                      canvasProps={{
                        width: isMobile ? 200 : 280,
                        height: isMobile ? 80 : 120,
                        style: { backgroundColor: '#fff', borderRadius: '4px', cursor: 'crosshair' }
                      }}
                      penColor="black"
                      minWidth={1}
                      maxWidth={2}
                    />
                  </Paper>
                )}
                {signatureMode === 'name' && (
                  <Box sx={{ width: '100%', maxWidth: 260 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Your Full Name"
                      value={typedName}
                      onChange={(e) => setTypedName(e.target.value)}
                      placeholder="Enter your full name"
                      variant="outlined"
                      sx={{
                        mb: 1.5,
                        '& .MuiInputBase-input': {
                          fontSize: '0.75rem',
                          textAlign: 'center',
                          fontFamily: 'cursive'
                        },
                        '& .MuiInputLabel-root': { fontSize: '0.65rem' }
                      }}
                    />
                    {typedName && (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.5,
                          bgcolor: '#f8f9fa',
                          border: `1px solid ${theme.palette.primary.main}20`,
                          textAlign: 'center'
                        }}
                      >
                        <Typography
                          sx={{ fontFamily: 'cursive', color: 'text.primary', fontSize: '0.9rem' }}
                        >
                          {typedName}
                        </Typography>
                      </Paper>
                    )}
                  </Box>
                )}
                {signatureMode === 'standard' && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: '#f8f9fa',
                      border: `1px solid ${theme.palette.primary.main}20`,
                      minWidth: 140
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isStandardChecked}
                          onChange={(e) => setIsStandardChecked(e.target.checked)}
                          sx={{
                            transform: 'scale(1.2)',
                            color: 'success.main',
                            '&.Mui-checked': { color: 'success.main' }
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ ml: 0.75, fontSize: '0.75rem' }}>
                          Standard Checkmark
                        </Typography>
                      }
                    />
                  </Paper>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ py: 0.75 }}>
              <TextField
                fullWidth
                size="small"
                value={tempFieldValue}
                onChange={(e) => setTempFieldValue(e.target.value)}
                type={
                  selectedField?.type === 'date'
                    ? 'date'
                    : selectedField?.type === 'email'
                    ? 'email'
                    : 'text'
                }
                autoFocus
                placeholder={`Enter ${selectedField?.type}`}
                sx={{
                  mb: 1,
                  '& .MuiInputBase-input': { fontSize: '0.7rem' }
                }}
                InputLabelProps={selectedField?.type === 'date' ? { shrink: true } : {}}
                helperText={
                  selectedField?.type === 'email'
                    ? 'Valid email address'
                    : selectedField?.type === 'date'
                    ? 'Select date'
                    : `Enter ${selectedField?.type} information`
                }
                FormHelperTextProps={{ sx: { fontSize: '0.55rem', mt: 0.5 } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 1.5, justifyContent: 'space-between' }}>
          <Button
            onClick={() => {
              if (selectedField?.type.toLowerCase() === 'signature') {
                if (signatureMode === 'pad' && signatureCanvasRef.current)
                  signatureCanvasRef.current.clear();
                else if (signatureMode === 'name') setTypedName('');
                else setIsStandardChecked(false);
              } else {
                setTempFieldValue('');
              }
            }}
            color="secondary"
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem' }}
          >
            Clear
          </Button>
          <Stack direction="row" spacing={0.75}>
            <Button
              onClick={() => setShowEditDialog(false)}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.65rem' }}
            >
              Cancel
            </Button>
            <Button
              onClick={
                selectedField?.type.toLowerCase() === 'signature'
                  ? handleSignatureSave
                  : handleFieldSave
              }
              color="primary"
              size="small"
              variant="contained"
              disabled={
                selectedField?.type.toLowerCase() === 'signature'
                  ? (signatureMode === 'pad' && signatureCanvasRef.current?.isEmpty()) ||
                    (signatureMode === 'name' && !typedName.trim()) ||
                    (signatureMode === 'standard' && !isStandardChecked)
                  : !tempFieldValue.trim()
              }
              sx={{ fontSize: '0.65rem' }}
            >
              Save
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      {/* Assignment Loading Dialog */}
      {templateAssigning && (
        <Dialog
          open={true}
          PaperProps={{
            sx: { bgcolor: 'rgba(255, 255, 255, 0.95)', boxShadow: 'none', borderRadius: 1.5 }
          }}
        >
          <DialogContent sx={{ textAlign: 'center', p: 3 }}>
            <CircularProgress size={isSmallMobile ? 40 : 50} sx={{ color: '#217346', mb: 1.5 }} />
            <Typography variant="h6" sx={{ mb: 0.75, fontSize: '0.8rem' }}>
              Assigning Template...
            </Typography>
            <Typography color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Processing assignment for {selectedEmployees.length} employee(s)
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
};

export default AssignTemplatePage;
